import React, { createContext, useContext, useState, useEffect } from 'react'
import localStorageService from '../services/localStorageService'
import backupService from '../services/backupService'
import toast from 'react-hot-toast'

/**
 * Local Mode Context for managing browser-only storage state.
 * 
 * Provides local storage operations, usage tracking, and sync capabilities
 * for users who choose to use the app without registration.
 */

const LocalModeContext = createContext({})

export const useLocalMode = () => {
  const context = useContext(LocalModeContext)
  if (!context) {
    throw new Error('useLocalMode must be used within a LocalModeProvider')
  }
  return context
}

export const LocalModeProvider = ({ children }) => {
  const [isLocalMode, setIsLocalMode] = useState(false)
  const [localNotes, setLocalNotes] = useState([])
  const [localFolders, setLocalFolders] = useState([])
  const [storageUsage, setStorageUsage] = useState({
    totalBytes: 0,
    notesCount: 0,
    foldersCount: 0,
    formatted: '0 bytes'
  })
  const [loading, setLoading] = useState(false)
  const [sessionId, setSessionId] = useState(null)
  const [sensitivePassword, setSensitivePasswordState] = useState(null)

  // Initialize local mode
  useEffect(() => {
    const initializeLocalMode = async () => {
      try {
        // Check if IndexedDB is supported first
        if (!('indexedDB' in window)) {
          console.warn('IndexedDB not supported in this browser')
          return
        }
        
        await localStorageService.initialize()
        setSessionId(localStorageService.getSessionId())
        
        // Load sensitive password from sessionStorage if available
        const storedPassword = sessionStorage.getItem('localSensitivePassword')
        if (storedPassword) {
          setSensitivePasswordState(storedPassword)
          console.log('🔐 LocalMode: Restored sensitive password from session')
        }
        
        // Check if local mode was explicitly enabled by checking localStorage flag
        const localModeEnabled = localStorage.getItem('localModeEnabled') === 'true'
        if (localModeEnabled) {
          console.log('🔍 LocalMode: Local mode was previously enabled, restoring...')
          setIsLocalMode(true)
          await loadLocalData()
        }
      } catch (error) {
        console.error('Failed to initialize local mode:', error)
      }
    }

    initializeLocalMode()
  }, [])

  /**
   * Enable local mode
   */
  const enableLocalMode = async () => {
    try {
      setLoading(true)
      
      // Check if IndexedDB is supported
      if (!('indexedDB' in window)) {
        toast.error('Your browser does not support local storage')
        return false
      }

      await localStorageService.initialize()
      setIsLocalMode(true)
      setSessionId(localStorageService.getSessionId())
      
      // Set flag in localStorage to remember local mode was enabled
      localStorage.setItem('localModeEnabled', 'true')
      
      await loadLocalData()
      
      // Create default folders if they don't exist
      await createDefaultLocalFolders()
      
      toast.success('Local mode enabled! Your notes are stored in your browser.')
      return true
      
    } catch (error) {
      console.error('Failed to enable local mode:', error)
      toast.error('Failed to enable local mode')
      return false
    } finally {
      setLoading(false)
    }
  }

  /**
   * Disable local mode (when syncing to cloud)
   */
  const disableLocalMode = () => {
    setIsLocalMode(false)
    setLocalNotes([])
    setLocalFolders([])
    setStorageUsage({
      totalBytes: 0,
      notesCount: 0,
      foldersCount: 0,
      formatted: '0 bytes'
    })
    setSessionId(null)
    
    // Clear the local mode flag from localStorage
    localStorage.removeItem('localModeEnabled')
  }

  /**
   * Load all local data
   */
  const loadLocalData = async () => {
    try {
      setLoading(true)
      
      // Clean up any duplicate folders first
      await backupService.cleanupLocalFolderDuplicates()
      
      // Get sensitive password for decryption
      const password = getSensitivePassword()
      
      const [notes, folders, usage] = await Promise.all([
        password 
          ? localStorageService.getAllNotesWithDecryption(password)
          : localStorageService.getAllNotes(),
        localStorageService.getAllFolders(),
        localStorageService.getStorageUsage()
      ])
      
      console.log('🔍 LocalModeContext: Loaded notes from service:', notes)
      console.log('🔍 LocalModeContext: Setting localNotes state to:', notes)
      
      setLocalNotes(notes)
      setLocalFolders(folders)
      setStorageUsage(usage)
      
      console.log('🔍 LocalModeContext: localNotes state should now be:', notes.length, 'notes')
      
    } catch (error) {
      console.error('Failed to load local data:', error)
      toast.error('Failed to load local data')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Create a new note in local storage
   */
  const createLocalNote = async (noteData) => {
    try {
      console.log('🔍 LocalModeContext: Creating local note:', noteData)
      console.log('🔍 LocalModeContext: Current localNotes length before creation:', localNotes.length)
      
      // Get sensitive password if creating in sensitive folder
      const password = getSensitivePassword()
      
      const note = await localStorageService.saveNote(noteData, noteData.folderId, password)
      console.log('✅ LocalModeContext: Note saved to IndexedDB:', note)
      
      setLocalNotes(prev => {
        const updated = [note, ...prev]
        console.log('✅ LocalModeContext: Updated localNotes state, new length:', updated.length)
        return updated
      })
      
      await updateStorageUsage()
      
      toast.success('Note saved locally')
      return note
      
    } catch (error) {
      console.error('Failed to create local note:', error)
      toast.error('Failed to save note')
      throw error
    }
  }

  /**
   * Update an existing note in local storage
   */
  const updateLocalNote = async (noteId, updates) => {
    try {
      console.log('🔍 LocalModeContext: Updating local note:', noteId, updates)
      console.log('🔍 LocalModeContext: Current localNotes length before update:', localNotes.length)
      
      const updatedNote = await localStorageService.updateNote(noteId, updates)
      console.log('✅ LocalModeContext: Note updated in IndexedDB:', updatedNote)
      
      setLocalNotes(prev => {
        const updated = prev.map(note => note.noteId === noteId ? updatedNote : note)
        console.log('✅ LocalModeContext: Updated localNotes state, length remains:', updated.length)
        return updated
      })
      
      await updateStorageUsage()
      
      toast.success('Note updated')
      return updatedNote
      
    } catch (error) {
      console.error('Failed to update local note:', error)
      toast.error('Failed to update note')
      throw error
    }
  }

  /**
   * Delete a note from local storage
   */
  const deleteLocalNote = async (noteId) => {
    try {
      await localStorageService.deleteNote(noteId)
      setLocalNotes(prev => prev.filter(note => note.noteId !== noteId))
      await updateStorageUsage()
      
      toast.success('Note deleted')
      return true
      
    } catch (error) {
      console.error('Failed to delete local note:', error)
      toast.error('Failed to delete note')
      throw error
    }
  }

  /**
   * Search local notes
   */
  const searchLocalNotes = async (query) => {
    try {
      return await localStorageService.searchNotes(query)
    } catch (error) {
      console.error('Failed to search local notes:', error)
      return []
    }
  }

  /**
   * Get a single local note by ID
   */
  const getLocalNote = async (noteId) => {
    try {
      return await localStorageService.getNoteById(noteId)
    } catch (error) {
      console.error('Failed to get local note:', error)
      return null
    }
  }

  /**
   * Create a new folder in local storage
   */
  const createLocalFolder = async (folderData) => {
    try {
      const folder = await localStorageService.saveFolder(folderData)
      setLocalFolders(prev => [...prev, folder])
      
      toast.success('Folder created')
      return folder
      
    } catch (error) {
      console.error('Failed to create local folder:', error)
      toast.error('Failed to create folder')
      throw error
    }
  }

  /**
   * Delete a folder from local storage
   */
  const deleteLocalFolder = async (folderId) => {
    try {
      await localStorageService.deleteFolder(folderId)
      setLocalFolders(prev => prev.filter(folder => folder.folderId !== folderId))
      
      return true
      
    } catch (error) {
      console.error('Failed to delete local folder:', error)
      toast.error('Failed to delete folder')
      throw error
    }
  }

  /**
   * Check if sensitive password is set for local mode
   */
  const hasSensitivePassword = () => {
    return !!sensitivePassword || !!sessionStorage.getItem('localSensitivePassword')
  }

  /**
   * Set sensitive password for local mode
   */
  const setSensitivePassword = (password) => {
    setSensitivePasswordState(password)
    sessionStorage.setItem('localSensitivePassword', password)
    console.log('🔐 LocalMode: Sensitive password set for session')
  }

  /**
   * Get sensitive password for local mode
   */
  const getSensitivePassword = () => {
    return sensitivePassword || sessionStorage.getItem('localSensitivePassword')
  }

  /**
   * Clear sensitive password (on logout or session end)
   */
  const clearSensitivePassword = () => {
    setSensitivePasswordState(null)
    sessionStorage.removeItem('localSensitivePassword')
    console.log('🔐 LocalMode: Sensitive password cleared')
  }

  /**
   * Update storage usage statistics
   */
  const updateStorageUsage = async () => {
    try {
      const usage = await localStorageService.getStorageUsage()
      setStorageUsage(usage)
      return usage
    } catch (error) {
      console.error('Failed to update storage usage:', error)
      return storageUsage
    }
  }

  /**
   * Get upgrade recommendations based on current usage
   */
  const getUpgradeRecommendations = async () => {
    try {
      if (!sessionId) return null

      const response = await fetch(
        `/api/local/session/${sessionId}/upgrade-options?storageBytes=${storageUsage.totalBytes}&notesCount=${storageUsage.notesCount}`
      )
      
      if (response.ok) {
        const data = await response.json()
        return data
      } else {
        console.warn('Failed to get upgrade recommendations')
        return null
      }
    } catch (error) {
      console.error('Failed to get upgrade recommendations:', error)
      return null
    }
  }

  /**
   * Export local data for cloud sync
   */
  const exportForSync = async () => {
    try {
      const exportData = await localStorageService.exportForSync()
      return exportData
    } catch (error) {
      console.error('Failed to export local data:', error)
      throw error
    }
  }

  /**
   * Sync local data to cloud
   */
  const syncToCloud = async (email, password, planType, cloudCredentials) => {
    try {
      setLoading(true)
      
      const exportData = await exportForSync()
      
      const response = await fetch(`/api/local/session/${sessionId}/sync-to-cloud`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          planType,
          cloudCredentials,
          localNotes: exportData.notes
        })
      })
      
      const result = await response.json()
      
      if (result.success) {
        // Clear local data after successful sync
        await localStorageService.clearAllData()
        disableLocalMode()
        
        toast.success('Successfully synced to cloud!')
        return result
      } else {
        toast.error('Failed to sync to cloud: ' + result.message)
        throw new Error(result.message)
      }
      
    } catch (error) {
      console.error('Failed to sync to cloud:', error)
      toast.error('Failed to sync to cloud')
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Clear all local data and reset local mode
   */
  const clearAllLocalData = async () => {
    try {
      console.log('🗑️ LocalModeContext: Clearing all local data')
      
      // Clear all data from IndexedDB
      await localStorageService.clearAllData()
      
      // Reset all local state
      setLocalNotes([])
      setLocalFolders([])
      setStorageUsage({
        notesCount: 0,
        attachmentsCount: 0,
        totalSizeBytes: 0,
        formatted: '0 MB'
      })
      
      // Clear session storage
      localStorage.removeItem('localSessionId')
      
      // Clear sensitive password to force setup on next local session
      clearSensitivePassword()
      
      // Disable local mode to reset the state
      disableLocalMode()
      
      // Clear any cached notes/folders state from other contexts
      try {
        window.dispatchEvent(new CustomEvent('localDataCleared', { 
          detail: { message: 'All local data has been cleared' } 
        }))
      } catch (_) {}
      
      console.log('✅ LocalModeContext: All local data cleared successfully')
      toast.success('All local notes deleted successfully')
      
      return true
    } catch (error) {
      console.error('❌ LocalModeContext: Failed to clear local data:', error)
      toast.error('Failed to delete local notes')
      throw error
    }
  }

  /**
   * Check if we should show upgrade suggestions
   */
  const shouldShowUpgradeSuggestion = () => {
    return (
      isLocalMode && 
      (storageUsage.notesCount >= 5 || storageUsage.totalBytes > 1024 * 1024) // > 1MB
    )
  }

  /**
   * Get current local mode status
   */
  const getLocalModeStatus = () => {
    return {
      isEnabled: isLocalMode,
      sessionId,
      storageUsage,
      notesCount: localNotes.length,
      foldersCount: localFolders.length,
      canSync: isLocalMode && localNotes.length > 0,
      shouldShowUpgrade: shouldShowUpgradeSuggestion()
    }
  }

  /**
   * Create default local folders (Pinned and Sensitive)
   */
  const createDefaultLocalFolders = async () => {
    try {
      console.log('🔍 LocalModeContext: Creating default local folders')
      
      // Check if default folders already exist (with better deduplication)
      const existingFolders = await localStorageService.getAllFolders()
      
      // Remove any duplicate folders by type first
      const foldersToKeep = []
      const seenTypes = new Set()
      
      for (const folder of existingFolders) {
        if (folder.folderType && !seenTypes.has(folder.folderType)) {
          seenTypes.add(folder.folderType)
          foldersToKeep.push(folder)
        } else if (folder.folderType && seenTypes.has(folder.folderType)) {
          // Delete duplicate folder
          console.log('🗑️ LocalModeContext: Removing duplicate folder:', folder.name)
          await localStorageService.deleteFolder(folder.folderId)
        }
      }
      
      const hasPinned = foldersToKeep.some(f => f.folderType === 'PINNED')
      const hasSensitive = foldersToKeep.some(f => f.folderType === 'SENSITIVE')
      
      if (hasPinned && hasSensitive) {
        console.log('✅ LocalModeContext: Default folders already exist')
        return false
      }
      
      const foldersToCreate = []
      
      if (!hasPinned) {
        foldersToCreate.push({
          folderId: 'folder-pinned-' + crypto.randomUUID(),
          name: 'Pinned',
          description: 'Important notes that need quick access',
          color: '#FFD700',
          folderType: 'PINNED',
          displayOrder: 1,
          isDefault: true,
          createdAt: new Date().toISOString(),
          lastModified: new Date().toISOString()
        })
      }
      
      if (!hasSensitive) {
        foldersToCreate.push({
          folderId: 'folder-sensitive-' + crypto.randomUUID(),
          name: 'Sensitive',
          description: 'Private and confidential notes',
          color: '#DC2626',
          folderType: 'SENSITIVE',
          displayOrder: 2,
          isDefault: true,
          createdAt: new Date().toISOString(),
          lastModified: new Date().toISOString()
        })
      }
      
      if (foldersToCreate.length === 0) {
        console.log('✅ LocalModeContext: No new folders needed')
        return false
      }
      
      // Save folders to local storage
      for (const folder of foldersToCreate) {
        await localStorageService.saveFolder(folder)
      }
      
      // Refresh folders list
      await loadLocalData()
      
      console.log('✅ LocalModeContext: Created default local folders:', foldersToCreate)
      
      return true
    } catch (error) {
      console.error('❌ LocalModeContext: Failed to create default folders:', error)
      toast.error('Failed to create default folders')
      throw error
    }
  }

  /**
   * Move a note to a different folder in local storage
   */
  const moveNoteToFolderLocal = async (noteId, folderId, password = null) => {
    try {
      console.log('🔍 LocalModeContext: Moving note to folder:', noteId, folderId)
      
      // Get the existing note
      const existingNote = await localStorageService.getNoteById(noteId)
      if (!existingNote) {
        throw new Error(`Note ${noteId} not found in local storage`)
      }
      
      // Update the note with new folder ID
      const updatedNote = {
        ...existingNote,
        folderId: folderId,
        lastModified: new Date().toISOString()
      }
      
      // Save the updated note (this will handle encryption if moving to sensitive folder)
      await localStorageService.saveNote(updatedNote, folderId, password)
      
      // Update local notes state
      setLocalNotes(prev => prev.map(note => 
        note.noteId === noteId 
          ? updatedNote
          : note
      ))
      
      const targetFolder = localFolders.find(f => f.folderId === folderId)
      const targetName = targetFolder ? targetFolder.name : 'No folder'
      console.log('✅ LocalModeContext: Note moved to:', targetName)
      
      return true
    } catch (error) {
      console.error('❌ LocalModeContext: Error moving note:', error)
      throw error
    }
  }

  /**
   * Upload file to local storage
   */
  const uploadFileLocal = async (file) => {
    try {
      console.log('📎 LocalModeContext: Uploading file locally:', file.name)
      
      // Use localStorageService to save the attachment
      const result = await localStorageService.saveAttachment(file)
      
      console.log('✅ LocalModeContext: File uploaded locally:', result.id)
      
      return result
    } catch (error) {
      console.error('❌ LocalModeContext: Error uploading file locally:', error)
      throw error
    }
  }

  /**
   * Get local file data (for downloads)
   */
  const getLocalFile = async (attachmentId) => {
    try {
      console.log('📎 LocalModeContext: Getting local file:', attachmentId)
      
      const attachment = await localStorageService.getAttachment(attachmentId)
      
      // Convert base64 back to blob for download
      const binaryString = atob(attachment.data)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      const blob = new Blob([bytes], { type: attachment.fileType })
      
      console.log('✅ LocalModeContext: Local file retrieved:', attachment.fileName)
      
      return {
        blob,
        fileName: attachment.fileName,
        fileType: attachment.fileType
      }
    } catch (error) {
      console.error('❌ LocalModeContext: Error getting local file:', error)
      throw error
    }
  }

  /**
   * Upload image file to local storage
   */
  const uploadImageFileLocal = async (file) => {
    try {
      console.log('🖼️ LocalModeContext: Uploading image locally:', file.name)
      
      // Use localStorageService to save the image attachment
      const result = await localStorageService.saveImageAttachment(file)
      
      console.log('✅ LocalModeContext: Image uploaded locally:', result.id)
      
      return result
    } catch (error) {
      console.error('❌ LocalModeContext: Error uploading image locally:', error)
      throw error
    }
  }

  /**
   * Get local image file data
   */
  const getLocalImageFile = async (attachmentId) => {
    try {
      console.log('🖼️ LocalModeContext: Getting local image:', attachmentId)
      
      const imageData = await localStorageService.getImageAttachment(attachmentId)
      
      console.log('✅ LocalModeContext: Local image retrieved:', imageData.fileName)
      
      return imageData
    } catch (error) {
      console.error('❌ LocalModeContext: Error getting local image:', error)
      throw error
    }
  }

  const value = {
    // State
    isLocalMode,
    localNotes,
    localFolders,
    storageUsage,
    loading,
    sessionId,
    
    // Actions
    enableLocalMode,
    disableLocalMode,
    loadLocalData,
    
    // Local operations
    createLocalNote,
    updateLocalNote,
    deleteLocalNote,
    searchLocalNotes,
    getLocalNote,
    
    // File operations
    uploadFileLocal,
    getLocalFile,
    
    // Image operations
    uploadImageFileLocal,
    getLocalImageFile,
    
    // Folder operations
    createLocalFolder,
    deleteLocalFolder,
    createDefaultLocalFolders,
    moveNoteToFolderLocal,
    
    // Storage management
    updateStorageUsage,
    clearAllLocalData,
    
    // Sensitive password management
    hasSensitivePassword,
    setSensitivePassword,
    getSensitivePassword,
    clearSensitivePassword,
    
    // Sync operations
    exportForSync,
    syncToCloud,
    getUpgradeRecommendations,
    
    // Status
    getLocalModeStatus,
    shouldShowUpgradeSuggestion
  }

  return (
    <LocalModeContext.Provider value={value}>
      {children}
    </LocalModeContext.Provider>
  )
}

export default LocalModeContext 